import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Combobox,
  Option,
  Input,
  Label,
  Text,
  Spinner,
  MessageBar,
  MessageBarBody,
  Badge,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { OpenRegular, CopyRegular } from '@fluentui/react-icons';
import { apiFetch } from '../lib/apiFetch';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    ...shorthands.padding('24px'),
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '520px',
    maxWidth: '100%',
    ...shorthands.padding('24px'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('4px'),
    boxShadow: tokens.shadow8,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  hint: {
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
  },
  balanceRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  result: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    ...shorthands.padding('16px'),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRadius('4px'),
  },
  linkRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'flex-end',
  },
  actions: {
    display: 'flex',
    gap: '8px',
  },
});

type OrderRow = {
  id: string;
  booqable_id: string | null;
  number: number | null;
  status: string | null;
  payment_status: string | null;
  grand_total_with_tax_in_cents: number | null;
  amount_paid_in_cents: number | null;
  customer?: { name?: string | null; email?: string | null };
};

const money = (cents: number) => `$${(cents / 100).toFixed(2)}`;
const balanceCents = (o: OrderRow) =>
  Math.max(0, (o.grand_total_with_tax_in_cents || 0) - (o.amount_paid_in_cents || 0));

const orderLabel = (o: OrderRow) => {
  const num = o.number != null ? `#${o.number}` : o.booqable_id?.slice(0, 8);
  const who = o.customer?.name || 'Unknown customer';
  return `${num} · ${who} · ${money(balanceCents(o))} due`;
};

export default function PayLinkExerciser() {
  const styles = useStyles();
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null); // booqable_id
  const [link, setLink] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ intent: 'success' | 'error'; text: string } | null>(null);

  // Only orders that exist in Booqable can be paid — the pay endpoints resolve
  // invoices against Booqable, so an un-promoted local draft has nothing to bill.
  useEffect(() => {
    apiFetch('/orders')
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to load orders');
        const rows: OrderRow[] = (data.data || []).filter((o: OrderRow) => o.booqable_id);
        setOrders(rows);
      })
      .catch((e) => setLoadError(e.message || 'Failed to load orders'));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = !q
      ? orders
      : orders.filter((o) => orderLabel(o).toLowerCase().includes(q));
    return list.slice(0, 100); // cap the rendered options; typeahead narrows further
  }, [orders, query]);

  const selected = orders.find((o) => o.booqable_id === selectedId) || null;

  // Combobox `value` is the visible text: the selected order's label once
  // chosen, otherwise the user's typeahead query (mirrors CompanyPicker).
  const displayValue = selectedId ? (selected ? orderLabel(selected) : '') : query;

  const handleGenerate = async () => {
    if (!selectedId) return;
    setBusy(true);
    setMsg(null);
    setLink('');
    try {
      const res = await apiFetch(`/payments/pay/link/${encodeURIComponent(selectedId)}`);
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Could not mint link');
      setLink(data.url);
    } catch (e: any) {
      setMsg({ intent: 'error', text: e.message || 'Could not mint link' });
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setMsg({ intent: 'success', text: 'Link copied to clipboard' });
    } catch {
      setMsg({ intent: 'error', text: 'Clipboard blocked — select and copy manually' });
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.form}>
        <Text size={500} weight="semibold">Pay-link exerciser</Text>
        <Text className={styles.hint}>
          Pick an order and mint a real customer "Pay Now" link (emails.build_payment_link).
          Opening it loads the branded /pay Accept.js page for that order's current balance.
          Mint and charge run against whichever environment this admin app points at.
        </Text>

        {loadError && (
          <MessageBar intent="error">
            <MessageBarBody>{loadError}</MessageBarBody>
          </MessageBar>
        )}

        <div className={styles.field}>
          <Label htmlFor="paylink-order">Order</Label>
          <Combobox
            id="paylink-order"
            size="large"
            freeform
            placeholder="Search by number, customer, or amount…"
            value={displayValue}
            selectedOptions={selectedId ? [selectedId] : []}
            onChange={(e) => {
              setSelectedId(null);
              setLink('');
              setQuery((e.target as HTMLInputElement).value);
            }}
            onOptionSelect={(_, d) => {
              const id = d.optionValue || null;
              setSelectedId(id);
              setLink('');
            }}
          >
            {filtered.map((o) => (
              <Option key={o.booqable_id!} value={o.booqable_id!} text={orderLabel(o)}>
                {orderLabel(o)}
              </Option>
            ))}
            {filtered.length === 0 && (
              <Option key="__empty__" value="" disabled>
                No matching orders
              </Option>
            )}
          </Combobox>
          <Text className={styles.hint}>
            {orders.length} payable order{orders.length === 1 ? '' : 's'} (have a Booqable id).
          </Text>
        </div>

        {selected && (
          <div className={styles.balanceRow}>
            <Badge appearance="tint" color={balanceCents(selected) > 0 ? 'warning' : 'success'}>
              {balanceCents(selected) > 0 ? `${money(balanceCents(selected))} due` : 'Paid in full'}
            </Badge>
            <Text className={styles.hint}>
              {selected.status || '—'} · {selected.payment_status || 'unknown'} · {selected.customer?.email || 'no email'}
            </Text>
          </div>
        )}

        <Button appearance="primary" onClick={handleGenerate} disabled={!selectedId || busy}>
          {busy ? <Spinner size="tiny" /> : 'Generate pay link'}
        </Button>

        {link && (
          <div className={styles.result}>
            <div className={styles.field}>
              <Label htmlFor="paylink-url">Pay link</Label>
              <div className={styles.linkRow}>
                <Input id="paylink-url" size="large" readOnly value={link} style={{ flex: 1 }} />
              </div>
            </div>
            <div className={styles.actions}>
              <Button
                appearance="primary"
                as="a"
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                icon={<OpenRegular />}
              >
                Open pay page
              </Button>
              <Button onClick={handleCopy} icon={<CopyRegular />}>Copy</Button>
            </div>
          </div>
        )}

        {msg && (
          <MessageBar intent={msg.intent}>
            <MessageBarBody>{msg.text}</MessageBarBody>
          </MessageBar>
        )}
      </div>
    </div>
  );
}
